import { isVocabularyDraft, type VocabularyDraftResponse } from "../../shared/vocabularyDraft";

export type VocabularySyncStatus = "local" | "loading" | "saving" | "saved" | "error" | "identity-error" | "conflict";
type Backup = { sourceText: string; revision: number; dirty: boolean };
const GUEST_KEY = "ziarcadia:vocabulary-review:draft";
const key = (owner: string) => `ziarcadia:vocabulary-review:account:${owner}`;

/** Lives outside the tool view so navigation never drops a queued save. */
export class VocabularyDraftSync {
    private owner: string | null | undefined;
    private generation = 0;
    private edits = 0;
    private backup: Backup = { sourceText: "", revision: 0, dirty: false };
    private ready = false;
    private timer: ReturnType<typeof setTimeout> | null = null;
    private writes: Promise<void> = Promise.resolve();
    private status: VocabularySyncStatus = "local";
    constructor(private readonly options: {
        apply: (sourceText: string) => void;
        status: (status: VocabularySyncStatus) => void;
    }) {}
    private setStatus(status: VocabularySyncStatus) { this.status = status; this.options.status(status); }
    setIdentityStatus(status: "loading" | "identity-error") {
        if (this.owner === undefined) this.setStatus(status);
    }
    private store() {
        try {
            if (this.owner) localStorage.setItem(key(this.owner), JSON.stringify(this.backup));
            else localStorage.setItem(GUEST_KEY, this.backup.sourceText);
        } catch { /* Cloud saves and review still work when storage is unavailable. */ }
    }
    private read(owner: string | null): Backup {
        try {
            if (!owner) return { sourceText: localStorage.getItem(GUEST_KEY) ?? "", revision: 0, dirty: false };
            const cached = JSON.parse(localStorage.getItem(key(owner)) ?? "null");
            if (cached && typeof cached.sourceText === "string" && cached.sourceText.length <= 80_000 && Number.isSafeInteger(cached.revision) && cached.revision >= 0) {
                return { sourceText: cached.sourceText, revision: cached.revision, dirty: cached.dirty === true };
            }
        } catch { /* Fall back to the cloud, never to another account's guest draft. */ }
        return { sourceText: "", revision: 0, dirty: false };
    }
    private async api(owner: string, write?: Backup) {
        const response = await fetch(`/api/account/vocabulary-review${write ? "" : `?accountId=${encodeURIComponent(owner)}`}`, {
            method: write ? "POST" : "GET", credentials: "same-origin", cache: "no-store", signal: AbortSignal.timeout(15_000),
            ...(write ? { headers: { "Content-Type": "application/json" }, body: JSON.stringify({ action: "save", accountId: owner, sourceText: write.sourceText, revision: write.revision }) } : {})
        });
        const result = await response.json() as VocabularyDraftResponse & { code?: string };
        if (result.accountId !== owner || (result.draft !== null && !isVocabularyDraft(result.draft))) throw new Error("Invalid draft response");
        if (response.status === 409 && result.code === "conflict") return { ...result, conflict: true };
        if (!response.ok) throw new Error("Draft request failed");
        return { ...result, conflict: false };
    }
    async restore(owner: string | null): Promise<void> {
        if (this.owner === owner) return;
        // These are edits typed in this page before its existing session was identified,
        // never a stored guest list or another account's backup.
        const pendingEdits = this.owner === undefined && this.edits > 0 ? this.backup.sourceText : undefined;
        if (this.timer) clearTimeout(this.timer);
        this.owner = owner;
        const generation = ++this.generation;
        this.edits = 0;
        this.ready = !owner;
        this.backup = this.read(owner);
        const hadDirtyBackup = pendingEdits === undefined && this.backup.dirty;
        if (pendingEdits !== undefined) {
            this.backup.sourceText = pendingEdits;
            this.backup.dirty = Boolean(owner);
            this.store();
        }
        this.options.apply(this.backup.sourceText);
        this.setStatus(owner ? "loading" : "local");
        if (!owner) return;
        const edits = this.edits;
        try {
            const result = await this.api(owner);
            if (generation !== this.generation) return;
            this.ready = true;
            if (this.backup.dirty || this.edits !== edits) {
                // Dirty offline edits retain their base revision; fresh edits during loading use this load's base.
                if (!hadDirtyBackup) this.backup.revision = result.draft?.revision ?? 0;
                this.store();
                await this.flush();
            } else {
                this.backup = { sourceText: result.draft?.sourceText ?? "", revision: result.draft?.revision ?? 0, dirty: false };
                this.store();
                this.options.apply(this.backup.sourceText);
                this.setStatus("saved");
            }
        } catch {
            if (generation === this.generation) this.setStatus("error");
        }
    }
    changed(sourceText: string) {
        ++this.edits;
        this.backup = { ...this.backup, sourceText, dirty: Boolean(this.owner) };
        if (this.owner === undefined) { this.setStatus(this.status === "identity-error" ? "identity-error" : "loading"); return; }
        this.store();
        if (!this.owner) { this.setStatus("local"); return; }
        this.setStatus(this.ready ? "saving" : this.status === "loading" ? "loading" : "error");
        if (this.timer) clearTimeout(this.timer);
        this.timer = setTimeout(() => { this.timer = null; void this.flush(); }, 600);
    }
    flush(): Promise<void> {
        if (this.timer) { clearTimeout(this.timer); this.timer = null; }
        const owner = this.owner;
        const generation = this.generation;
        this.writes = this.writes.then(async () => {
            if (!owner || generation !== this.generation || !this.ready || !this.backup.dirty) return;
            const write = { ...this.backup };
            const edits = this.edits;
            this.setStatus("saving");
            try {
                const result = await this.api(owner, write);
                if (generation !== this.generation) return;
                if (result.conflict) {
                    this.setStatus("conflict");
                    return;
                }
                if (!result.draft) throw new Error("Missing draft");
                this.backup.revision = result.draft.revision;
                if (edits === this.edits) this.backup.dirty = false;
                this.store();
                this.setStatus(this.backup.dirty ? "saving" : "saved");
                if (this.backup.dirty) this.timer = setTimeout(() => { this.timer = null; void this.flush(); }, 0);
            } catch { if (generation === this.generation) this.setStatus("error"); }
        });
        return this.writes;
    }
    async retry() {
        const owner = this.owner;
        if (!owner) return;
        if (!this.ready || this.status === "conflict") {
            const generation = this.generation;
            try {
                const result = await this.api(owner);
                if (generation !== this.generation) return;
                this.ready = true;
                if (!this.backup.dirty) {
                    this.backup = { sourceText: result.draft?.sourceText ?? "", revision: result.draft?.revision ?? 0, dirty: false };
                    this.options.apply(this.backup.sourceText);
                } else this.backup.revision = result.draft?.revision ?? 0;
                this.store();
            } catch { if (generation === this.generation) this.setStatus("error"); return; }
        }
        await this.flush();
        if (!this.backup.dirty) this.setStatus("saved");
    }
}
